import {
  LayoutDashboard,
  BarChart3,
  Calendar,
  Eye,
  TrendingUp,
  PieChart,
  Repeat,
  Settings2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FeatureLayout from '../../components/layout/FeatureLayout';
import SEOHead from '../../components/common/SEOHead';

const sectionKeys = [
  { icon: BarChart3, key: 'widgets' },
  { icon: Calendar, key: 'views' },
  { icon: TrendingUp, key: 'trends' },
  { icon: PieChart, key: 'categories' },
  { icon: Repeat, key: 'forecast' },
  { icon: Settings2, key: 'customization' },
];

export default function FeatureDashboard() {
  const { t } = useTranslation('features');

  return (
    <FeatureLayout
      breadcrumbs={[{ label: t('dashboard.title') }]}
    >
      <SEOHead
        title="Dashboard intelligent - Tableau de bord financier"
        description="Visualisez vos finances personnelles et de couple en un coup d'oeil. Widgets personnalisables, graphiques de tendance, prévisions et vue par catégories."
        path="/fonctionnalites/dashboard"
      />
      {/* Hero */}
      <section className="pub-hero py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="pub-ico w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <LayoutDashboard className="w-8 h-8" />
          </div>
          <h1 className="pub-title text-3xl sm:text-4xl font-bold mb-4">
            {t('dashboard.title')}
          </h1>
          <p className="pub-muted text-lg max-w-2xl mx-auto">
            {t('dashboard.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid gap-8">
            {sectionKeys.map((section, index) => (
              <div
                key={section.key}
                className={`flex flex-col md:flex-row items-start gap-6 p-6 rounded-xl ${
                  index % 2 === 0 ? 'pub-card' : 'pub-section-alt'
                }`}
              >
                <div className="pub-ico w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="pub-title text-lg font-semibold mb-2">
                    {t(`dashboard.sections.${section.key}.title`)}
                  </h3>
                  <p className="pub-muted">{t(`dashboard.sections.${section.key}.description`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pub-section-alt py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="pub-title text-2xl font-bold mb-8 text-center">
            {t('dashboard.twoSpaces')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="pub-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold">{t('dashboard.personalSpace')}</h3>
              </div>
              <ul className="pub-muted space-y-2 text-sm">
                <li>{t('dashboard.personalItems.balance')}</li>
                <li>{t('dashboard.personalItems.incomeExpenses')}</li>
                <li>{t('dashboard.personalItems.savingsRate')}</li>
                <li>{t('dashboard.personalItems.recentOps')}</li>
                <li>{t('dashboard.personalItems.budgets')}</li>
              </ul>
            </div>
            <div className="pub-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold">{t('dashboard.sharedSpace')}</h3>
              </div>
              <ul className="pub-muted space-y-2 text-sm">
                <li>{t('dashboard.sharedItems.balance')}</li>
                <li>{t('dashboard.sharedItems.split')}</li>
                <li>{t('dashboard.sharedItems.categories')}</li>
                <li>{t('dashboard.sharedItems.partnerDetails')}</li>
                <li>{t('dashboard.sharedItems.budgets')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </FeatureLayout>
  );
}
